import { cn } from './utils';

/**
 * Color-coded badge for the condition_band enum. Used in tables and asset
 * detail headers. The "unknown" case visually de-emphasizes itself so
 * it doesn't compete with real signals.
 */

const LABELS: Record<string, string> = {
  new: 'New',
  like_new: 'Like new',
  normal_enterprise_wear: 'Normal wear',
  high_wear: 'High wear',
  // SIL-1534: buyer-facing label for the 'defective' condition_band value -
  // customer marketplace card/detail and the public exchange all route
  // through this one map (no separate buyer-side copy to keep in sync).
  defective: 'Defective / for parts',
  unknown: 'Condition not set',
};

// Canonical unknown/fallback style — keep in sync with LifecycleBadge's
// fallback. Do not diverge; this was previously drifted across copies.
const STYLES: Record<string, string> = {
  // SIL-322: migration 621 added 'new' to condition_band. Per DESIGN.md's
  // Chrome-and-State Rule, violet is chrome only and must never encode a
  // condition/verdict — 'new' and 'like_new' are both good-condition states,
  // so both use the sanctioned green state hue; the label text (not color)
  // distinguishes them.
  new: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  like_new: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  normal_enterprise_wear: 'bg-muted text-foreground ring-border',
  high_wear: 'bg-amber-50 text-amber-700 ring-amber-200',
  // SIL-1534: worse than high_wear - red, the sanctioned "needs attention"
  // state hue, distinct from amber (wear) and the green good-condition hues.
  defective: 'bg-red-50 text-red-700 ring-red-200',
  unknown: 'bg-muted text-muted-foreground ring-border',
};

/** Human label for a condition_band id (shared with non-badge surfaces). */
export function conditionLabel(value: string | null | undefined): string {
  const key = value ?? 'unknown';
  return LABELS[key] ?? key;
}

export function ConditionBadge({ value }: { value: string | null | undefined }) {
  const key = value ?? 'unknown';
  const label = LABELS[key] ?? key;
  const style = STYLES[key] ?? STYLES.unknown;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        style,
      )}
    >
      {label}
    </span>
  );
}
